import React, { useState, useEffect } from 'react';
import { Stethoscope, Plus, AlertCircle, Pill, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import api from '../lib/api';

function MedicalPage() {
  const [records, setRecords] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [newRecord, setNewRecord] = useState({ student_id: '', record_type: 'visite', date: '', title: '', description: '', treatment: '' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([api.get('/medical-records'), api.get('/students')]);
      setRecords(r.data);
      setStudents(s.data.filter(st => st.boarding_type === 'interne'));
    } catch (e) { toast.error('Erreur'); }
    finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newRecord.student_id || !newRecord.title) { toast.error('Champs requis'); return; }
    setFormLoading(true);
    try {
      await api.post('/medical-records', newRecord);
      toast.success('Enregistrement cree');
      setShowDialog(false);
      setNewRecord({ student_id: '', record_type: 'visite', date: '', title: '', description: '', treatment: '' });
      fetchData();
    } catch (e) { toast.error('Erreur'); }
    finally { setFormLoading(false); }
  };

  const getStudentName = (id) => { const s = students.find(st => st.id === id); return s ? s.first_name + ' ' + s.last_name : '-'; };

  const renderStudents = () => {
    if (students.length === 0) return <p className="text-center text-muted-foreground py-8">Aucun interne</p>;
    const items = [];
    for (let i = 0; i < students.length; i++) {
      const student = students[i];
      items.push(
        <div key={student.id} className="p-3 rounded-lg border hover:border-primary cursor-pointer">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-xs font-bold text-primary">{student.first_name[0]}{student.last_name[0]}</span>
            </div>
            <div>
              <p className="font-medium text-sm">{student.first_name} {student.last_name}</p>
              <p className="text-xs text-muted-foreground">{student.matricule}</p>
            </div>
          </div>
        </div>
      );
    }
    return items;
  };

  const renderRecords = () => {
    if (records.length === 0) return <p className="text-center text-muted-foreground py-8">Aucun enregistrement</p>;
    const items = [];
    for (let i = 0; i < Math.min(records.length, 20); i++) {
      const record = records[i];
      items.push(
        <div key={record.id} className="p-3 border rounded-lg">
          <div className="flex items-start justify-between mb-2">
            <Badge className={record.record_type === 'allergie' ? 'bg-red-100 text-red-700' : record.record_type === 'traitement' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
              {record.record_type}
            </Badge>
            <span className="text-xs text-muted-foreground">{record.date}</span>
          </div>
          <p className="font-medium">{record.title}</p>
          <p className="text-sm text-muted-foreground">{getStudentName(record.student_id)}</p>
        </div>
      );
    }
    return items;
  };

  const renderStudentOptions = () => {
    const items = [];
    for (let i = 0; i < students.length; i++) {
      const s = students[i];
      items.push(<SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>);
    }
    return items;
  };

  return (
    <div className="space-y-6" data-testid="medical-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div><h1 className="text-2xl font-bold">Suivi Medical</h1><p className="text-muted-foreground text-sm">Gestion des dossiers medicaux</p></div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild><Button className="rounded-full"><Plus className="w-4 h-4 mr-2" />Nouvel enregistrement</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouvel enregistrement</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Eleve *</Label><Select value={newRecord.student_id} onValueChange={(v) => setNewRecord({...newRecord, student_id: v})}><SelectTrigger><SelectValue placeholder="Selectionner" /></SelectTrigger><SelectContent>{renderStudentOptions()}</SelectContent></Select></div>
                <div className="space-y-2"><Label>Type *</Label><Select value={newRecord.record_type} onValueChange={(v) => setNewRecord({...newRecord, record_type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="visite">Visite</SelectItem><SelectItem value="traitement">Traitement</SelectItem><SelectItem value="allergie">Allergie</SelectItem><SelectItem value="vaccination">Vaccination</SelectItem></SelectContent></Select></div>
              </div>
              <div className="space-y-2"><Label>Date</Label><Input type="date" value={newRecord.date} onChange={(e) => setNewRecord({...newRecord, date: e.target.value})} /></div>
              <div className="space-y-2"><Label>Titre *</Label><Input value={newRecord.title} onChange={(e) => setNewRecord({...newRecord, title: e.target.value})} placeholder="Ex: Fievre" /></div>
              <div className="space-y-2"><Label>Description</Label><Textarea value={newRecord.description} onChange={(e) => setNewRecord({...newRecord, description: e.target.value})} rows={2} /></div>
              <div className="space-y-2"><Label>Traitement</Label><Input value={newRecord.treatment} onChange={(e) => setNewRecord({...newRecord, treatment: e.target.value})} placeholder="Medicaments..." /></div>
              <div className="flex justify-end gap-2 pt-4"><Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Annuler</Button><Button type="submit" disabled={formLoading}>{formLoading ? 'Creation...' : 'Creer'}</Button></div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card><CardHeader><CardTitle className="text-lg">Eleves internes</CardTitle></CardHeader><CardContent className="max-h-[500px] overflow-y-auto space-y-2">{loading ? <Skeleton className="h-40" /> : renderStudents()}</CardContent></Card>
        <Card className="lg:col-span-2"><CardHeader><CardTitle className="text-lg">Derniers enregistrements</CardTitle></CardHeader><CardContent className="max-h-[500px] overflow-y-auto space-y-3">{loading ? <Skeleton className="h-40" /> : renderRecords()}</CardContent></Card>
      </div>
    </div>
  );
}
export default MedicalPage;
